import { styled } from '@arpeggi/ui'
import { FC, SVGProps } from 'react'

const Root = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  paddingBottom: '32px',
  gap: '24px',
  '@tablet': { flexDirection: 'row' }
})

const Card = styled('div', {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: '16px 24px',
  gap: '24px',
  background: '$white',
  userSelect: 'none',
  cursor: 'pointer',
  borderRadius: 12,
  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
  transition: 'all 100ms ease',
  '&:hover': {
    boxShadow: '0px 6px 24px 0px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)'
  },
  '&:active': {
    boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(1px)'
  }
})

const Icon = styled('svg', {
  width: '32px',
  height: '32px',
  flexShrink: 0
})

const Soundwave: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.3965 4.00004C17.3965 3.22684 16.7697 2.60004 15.9965 2.60004C15.2233 2.60004 14.5965 3.22684 14.5965 4.00004L14.5965 28C14.5965 28.7732 15.2233 29.4 15.9965 29.4C16.7697 29.4 17.3965 28.7732 17.3965 28L17.3965 4.00004ZM9.33241 7.93347C10.1056 7.93347 10.7324 8.56027 10.7324 9.33347L10.7324 22.6668C10.7324 23.44 10.1056 24.0668 9.33241 24.0668C8.55921 24.0668 7.93241 23.44 7.93241 22.6668L7.93241 9.33347C7.93241 8.56027 8.55921 7.93347 9.33241 7.93347ZM2.66611 13.2666C3.43931 13.2666 4.06611 13.8934 4.06611 14.6666L4.06611 17.3333C4.06611 18.1065 3.43931 18.7333 2.66611 18.7333C1.89291 18.7333 1.26611 18.1065 1.26611 17.3333L1.26611 14.6666C1.26611 13.8934 1.89291 13.2666 2.66611 13.2666ZM22.6661 7.93347C23.4393 7.93347 24.0661 8.56027 24.0661 9.33347L24.0661 22.6668C24.0661 23.44 23.4393 24.0668 22.6661 24.0668C21.8929 24.0668 21.2661 23.44 21.2661 22.6668L21.2661 9.33347C21.2661 8.56027 21.8929 7.93347 22.6661 7.93347ZM30.7324 14.6666C30.7324 13.8934 30.1056 13.2666 29.3324 13.2666C28.5592 13.2666 27.9324 13.8934 27.9324 14.6666L27.9324 17.3333C27.9324 18.1065 28.5592 18.7333 29.3324 18.7333C30.1056 18.7333 30.7324 18.1065 30.7324 17.3333L30.7324 14.6666Z"
      fill="url(#paint0_linear_1026_43819)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1026_43819"
        x1="15.9993"
        y1="2.60004"
        x2="15.9993"
        y2="29.4"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#ECC94B" />
        <stop offset="1" stopColor="#ABE5D8" />
      </linearGradient>
    </defs>
  </svg>
)

const Repeat: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M5.0481 24.7154H21.504C23.2853 24.7154 24.1759 24.7154 24.8563 24.3687C25.4547 24.0638 25.9413 23.5772 26.2462 22.9788C26.5929 22.2984 26.5929 21.4078 26.5929 19.6265V18.3305M5.0481 24.7154L8.63889 21.5229M5.0481 24.7154L8.63889 27.9078M26.9576 7.10875H10.5017C8.72042 7.10875 7.82979 7.10875 7.14944 7.45541C6.55099 7.76034 6.06443 8.2469 5.7595 8.84535C5.41284 9.52571 5.41284 10.4163 5.41284 12.1976V13.4936M26.9576 7.10875L23.3668 3.91632M26.9576 7.10875L23.3668 10.3012"
      stroke="url(#paint0_linear_1026_43831)"
      strokeWidth="2.46154"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1026_43831"
        x1="16.0029"
        y1="3.91632"
        x2="16.0029"
        y2="27.9078"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#6B70E0" />
        <stop offset="1" stopColor="#9DA0FB" />
      </linearGradient>
    </defs>
  </svg>
)

const Create: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.8095 2.96295C2.80791 2.96295 1.1853 4.58556 1.1853 6.58715V19.7417C1.1853 21.7433 2.80791 23.3659 4.8095 23.3659H15.6916V25.165V27.2931H9.51641V28.7331H23.9164V27.2931H17.7487V25.165V23.3659H27.4455C29.4471 23.3659 31.0697 21.7433 31.0697 19.7417V6.58715C31.0697 4.58556 29.4471 2.96295 27.4455 2.96295H4.8095ZM2.6253 6.58715C2.6253 5.38085 3.6032 4.40295 4.8095 4.40295H27.4455C28.6518 4.40295 29.6297 5.38085 29.6297 6.58715V19.7417C29.6297 20.948 28.6518 21.9259 27.4455 21.9259H4.8095C3.6032 21.9259 2.6253 20.948 2.6253 19.7417V6.58715ZM17.5429 8.71989C17.5429 8.26544 17.1745 7.89704 16.7201 7.89704C16.2656 7.89704 15.8972 8.26544 15.8972 8.71989V17.6088C15.8972 18.0632 16.2656 18.4316 16.7201 18.4316C17.1745 18.4316 17.5429 18.0632 17.5429 17.6088V8.71989ZM13.0164 9.87237C13.4708 9.87237 13.8392 10.2408 13.8392 10.6952V15.6335C13.8392 16.088 13.4708 16.4564 13.0164 16.4564C12.5619 16.4564 12.1935 16.088 12.1935 15.6335V10.6952C12.1935 10.2408 12.5619 9.87237 13.0164 9.87237ZM9.31265 11.8477C9.7671 11.8477 10.1355 12.2161 10.1355 12.6706V13.6582C10.1355 14.1127 9.7671 14.4811 9.31265 14.4811C8.8582 14.4811 8.48979 14.1127 8.48979 13.6582V12.6706C8.48979 12.2161 8.8582 11.8477 9.31265 11.8477ZM20.4238 9.87237C20.8782 9.87237 21.2466 10.2408 21.2466 10.6952V15.6335C21.2466 16.0879 20.8782 16.4564 20.4238 16.4564C19.9693 16.4564 19.6009 16.0879 19.6009 15.6335V10.6952C19.6009 10.2408 19.9693 9.87237 20.4238 9.87237ZM24.9503 12.6706C24.9503 12.2161 24.5819 11.8477 24.1275 11.8477C23.673 11.8477 23.3046 12.2161 23.3046 12.6706V13.6582C23.3046 14.1127 23.673 14.4811 24.1275 14.4811C24.5819 14.4811 24.9503 14.1127 24.9503 13.6582V12.6706Z"
      fill="url(#paint0_linear_1026_43838)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1026_43838"
        x1="16.1275"
        y1="2.96295"
        x2="16.1275"
        y2="28.7331"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F49A30" />
        <stop offset="1" stopColor="#F0B4F1" />
      </linearGradient>
    </defs>
  </svg>
)

const Main = styled('div', { flex: 1 })

const Title = styled('div', {
  color: '$black',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  text: '18',
  fontWeight: 500,
  marginBottom: '4px'
})

const Subtitle = styled('div', {
  color: '$blackA400',
  text: '12'
})

export const Actions = { Root, Card, Icon, Create, Repeat, Soundwave, Main, Title, Subtitle }
